/**
 * AppSidebar — role-aware collapsible sidebar with tenant switcher.
 */

import { useEffect } from 'react';
import {
  Building2,
  LayoutDashboard,
  Users,
  FileText,
  ScrollText,
  Clock,
  Shield,
  Settings,
  MapPin,
  UserPlus,
  ClipboardList,
  History,
  User,
  Briefcase,
  FolderOpen,
} from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/use-auth';
import { isAdmin } from '@/lib/permissions';
import { ROUTES } from '@/config/constants';
import { useTenants } from '@/hooks/use-tenants';
import { useSidebar } from '@/components/ui/sidebar-context';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
} from '@/components/ui/sidebar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const adminNavItems = [
  { title: 'Dashboard', url: ROUTES.DASHBOARD, icon: LayoutDashboard },
  { title: 'Employees', url: ROUTES.EMPLOYEES, icon: Users },
  { title: 'Invitations', url: ROUTES.INVITATIONS, icon: UserPlus },
  { title: 'Documents', url: ROUTES.DOCUMENTS, icon: FileText },
  { title: 'Contracts', url: ROUTES.CONTRACTS, icon: ScrollText },
  { title: 'Attendance', url: ROUTES.ATTENDANCE_ADMIN, icon: Clock },
  { title: 'Reviews', url: ROUTES.REVIEWS, icon: ClipboardList },
  { title: 'Audit Log', url: ROUTES.AUDIT_LOG, icon: History },
];

const adminSettingsItems = [
  { title: 'Organization', url: ROUTES.ORGANIZATION, icon: Building2 },
  { title: 'Tenants', url: ROUTES.TENANTS, icon: Shield },
  { title: 'Policies', url: ROUTES.POLICIES, icon: MapPin },
  { title: 'Settings', url: ROUTES.SETTINGS, icon: Settings },
];

const employeeNavItems = [
  { title: 'Dashboard', url: ROUTES.DASHBOARD, icon: LayoutDashboard },
  { title: 'My Profile', url: ROUTES.MY_PROFILE, icon: User },
  { title: 'My Time', url: ROUTES.MY_TIME, icon: Clock },
  { title: 'My Documents', url: ROUTES.MY_DOCUMENTS, icon: FolderOpen },
  { title: 'My Contracts', url: ROUTES.MY_CONTRACTS, icon: Briefcase },
  { title: 'Settings', url: ROUTES.MY_SETTINGS, icon: Settings },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const { activeRole, activeTenantId, setActiveTenant, profile } = useAuth();
  const { data: tenants } = useTenants();
  const admin = isAdmin(activeRole);

  useEffect(() => {
    if (!admin || !tenants?.length || !activeTenantId) {
      return;
    }

    const hasActiveTenantSelected = tenants.some((tenant) => tenant.id === activeTenantId);

    if (!hasActiveTenantSelected) {
      setActiveTenant(tenants[0].id);
    }
  }, [activeTenantId, admin, setActiveTenant, tenants]);

  const navItems = admin ? adminNavItems : employeeNavItems;

  return (
    <Sidebar collapsible="icon" aria-label="Main navigation">
      <SidebarHeader className="border-b border-sidebar-border p-3">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
              E
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-sidebar-foreground truncate">Employee TMS</p>
              <p className="text-xs text-muted-foreground truncate">{profile?.full_name || profile?.email}</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
              E
            </div>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent>
        {/* Tenant Switcher — admin only */}
        {admin && !collapsed && tenants && tenants.length > 1 && (
          <div className="p-3 pb-0">
            <Select value={activeTenantId ?? ''} onValueChange={setActiveTenant}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-xs">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Main Nav */}
        <SidebarGroup role="navigation" aria-label={admin ? 'Management navigation' : 'Main navigation'}>
          <SidebarGroupLabel>{admin ? 'Management' : 'Menu'}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === ROUTES.DASHBOARD}
                      className="hover:bg-sidebar-accent/50"
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                    >
                      <item.icon className="mr-2 h-4 w-4 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin Settings */}
        {admin && (
          <SidebarGroup role="navigation" aria-label="Administration navigation">
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminSettingsItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className="hover:bg-sidebar-accent/50"
                        activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                      >
                        <item.icon className="mr-2 h-4 w-4 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        {!collapsed && (
          <p className="text-[10px] text-muted-foreground text-center">
            {admin ? 'Admin' : 'Employee'} · v1.0
          </p>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
